import { ReactNode, SyntheticEvent, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { plural, t } from '@lingui/macro'
import { useLingui } from '@lingui/react'

import Autocomplete, { AutocompleteChangeReason } from '@mui/material/Autocomplete'
import { PopperProps } from '@mui/material/Popper'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import PersonIcon from '@mui/icons-material/Person'
import SearchIcon from '@mui/icons-material/Search'
import VideoCallIcon from '@mui/icons-material/VideoCall'

import { useSidebarItems } from '@/layouts/main/model/useSidebarItems'
import { useElaraPanel } from '@/providers/elara/ElaraPanelContext'
import { SidebarType } from '@/utils/subscription/types'

import { DictationError, isDictationSupported, useDictation } from '../hooks/useDictation'
import { useOmniboxPatients } from '../hooks/useOmniboxPatients'
import { OmniboxInputTools } from './OmniboxInputTools'
import { buildOmniboxActions, OmniboxCreateAction, pickSuggestedActions } from '../lib/omnibox-actions'
import { buildOmniboxOptions, OmniboxOption } from '../lib/omnibox-options'

function createActions(): OmniboxCreateAction[] {
  return [
    { id: 'create-patient', label: t`Create a patient`, link: '/patients/create', icon: PersonAddAlt1Icon, requires: SidebarType.Patients },
    {
      id: 'create-consultation',
      label: t`Start a new consultation`,
      link: '/consultations/create',
      icon: VideoCallIcon,
      requires: SidebarType.Consultations,
    },
  ]
}

function optionLabel(option: OmniboxOption | string): string {
  if (typeof option === 'string') return option
  if (option.kind === 'action') return option.action.label
  if (option.kind === 'patient') return option.patient.fullName
  return option.query
}

function optionKey(option: OmniboxOption): string {
  if (option.kind === 'action') return `action-${option.action.id}`
  if (option.kind === 'patient') return `patient-${option.patient.id}`
  return 'elara'
}

function OptionContent({ option }: { option: OmniboxOption }): JSX.Element {
  if (option.kind === 'action') {
    const Icon = option.action.icon
    return (
      <>
        <Icon sx={{ color: 'primary.main', mr: 1.5 }} />
        <Typography fontSize="1rem">{option.action.label}</Typography>
      </>
    )
  }
  if (option.kind === 'patient') {
    return (
      <>
        <PersonIcon sx={{ color: 'text.secondary', mr: 1.5 }} />
        <Typography fontSize="1rem" fontWeight={500}>
          {option.patient.fullName}
        </Typography>
        {option.patient.birthday && (
          <Typography color="text.secondary" fontSize="0.9rem" sx={{ ml: 1 }}>
            {option.patient.birthday}
          </Typography>
        )}
      </>
    )
  }
  return (
    <>
      <AutoAwesomeIcon sx={{ color: 'secondary.main', mr: 1.5 }} />
      <Typography fontSize="1rem">
        {option.attachmentCount ? plural(option.attachmentCount, { one: 'Ask Elara (with # file)', other: 'Ask Elara (with # files)' }) : t`Ask Elara:`}
        {option.query && <strong> « {option.query} »</strong>}
      </Typography>
    </>
  )
}

const DICTATION_ERRORS: Record<DictationError, () => string> = {
  permission: () => t`Microphone access was refused. Allow it in your browser to dictate.`,
  failed: () => t`Dictation did not work. Please try again, or type your request.`,
  empty: () => t`Nothing was heard. Please try again.`,
}

/** Recording / transcribing / error line and the attached files. */
function OmniboxStatus({
  dictation,
  attachments,
  onRemove,
}: {
  dictation: { state: string; error: DictationError | null }
  attachments: File[]
  onRemove: (index: number) => void
}): JSX.Element {
  return (
    <>
      {dictation.state === 'recording' && (
        <Typography role="status" color="error.main" fontWeight={500} sx={{ mt: 1.5 }}>
          {t`Listening… tap the microphone again when you are done.`}
        </Typography>
      )}
      {dictation.state === 'transcribing' && (
        <Typography role="status" color="text.secondary" sx={{ mt: 1.5 }}>
          {t`Transcribing…`}
        </Typography>
      )}
      {dictation.state === 'idle' && dictation.error && (
        <Typography role="alert" color="error.main" sx={{ mt: 1.5 }}>
          {DICTATION_ERRORS[dictation.error]()}
        </Typography>
      )}
      {attachments.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
          {attachments.map((file, index) => (
            <Chip key={`${file.name}-${index}`} label={file.name} onDelete={() => onRemove(index)} variant="outlined" />
          ))}
        </Stack>
      )}
    </>
  )
}

export type OmniboxSearchProps = {
  /** Focus the field on mount (pop-up from the top bar). */
  autoFocus?: boolean
  /** Called after a result or shortcut was used, e.g. to close the pop-up. */
  onDone?: () => void
  /** Show results inside the surrounding container (pop-up) instead of a floating list over the page. */
  inlineResults?: boolean
}

/** Renders the result list in the normal flow, so a pop-up grows with it instead of being overflowed. */
function InlineResults({ open, children }: PopperProps): JSX.Element | null {
  return open ? <Box sx={{ mt: 1, '& .MuiAutocomplete-listbox': { maxHeight: 'none' } }}>{children as ReactNode}</Box> : null
}

/**
 * The search itself, shared by the dashboard card and the top-bar pop-up: type what you want
 * ("patient", "salle d'attente", a patient's name, or a question) and pick a result.
 * Navigation and patient results are deterministic; free text goes to Elara only when chosen.
 */
export function OmniboxSearch({ autoFocus = false, onDone, inlineResults = false }: OmniboxSearchProps): JSX.Element {
  useLingui()
  const { t: translate } = useTranslation()
  const navigate = useNavigate()
  const elaraPanel = useElaraPanel()
  const { sidebarItems, canUseElaraChat } = useSidebarItems()
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [attachments, setAttachments] = useState<File[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  const canAskElara = canUseElaraChat && Boolean(elaraPanel)
  const canSearchPatients = sidebarItems.some((item) => item.name === SidebarType.Patients)

  const actions = useMemo(() => buildOmniboxActions(sidebarItems, createActions(), translate), [sidebarItems, translate])
  const suggestions = useMemo(() => pickSuggestedActions(actions), [actions])
  const patientSearch = useOmniboxPatients(inputValue, canSearchPatients)
  const options: OmniboxOption[] = buildOmniboxOptions(inputValue, actions, patientSearch, canAskElara, attachments.length)

  /** Shows results for text that did not come from typing (dictation, attachments). */
  function showResults(): void {
    setIsOpen(true)
    inputRef.current?.focus()
  }

  const dictation = useDictation((text) => {
    setInputValue(text)
    showResults()
  })

  function attach(files: File[]): void {
    setAttachments((current) => [...current, ...files])
    // The file picker hands focus back to the paperclip after this handler; reopen once it has.
    window.setTimeout(showResults, 0)
  }
  const showNoResult: boolean = inputValue.trim().length > 0 && !patientSearch.isSearching && options.length === 0

  function goTo(link: string): void {
    navigate(link)
    onDone?.()
  }

  function runOption(option: OmniboxOption): void {
    setInputValue('')
    setAttachments([])
    setIsOpen(false)
    onDone?.()
    if (option.kind === 'action') navigate(option.action.link)
    else if (option.kind === 'patient') navigate(`/patients/${option.patient.id}`)
    else elaraPanel?.askElara(option.query || (option.attachmentCount ? t`What can you tell me about this document?` : ''), attachments)
  }

  function handleChange(_event: SyntheticEvent, value: OmniboxOption | string | null, reason: AutocompleteChangeReason): void {
    if (!value) return
    if (typeof value !== 'string') return runOption(value)
    // Free text is never sent directly: Enter can only select a ranked result.
    if (reason === 'createOption' && options[0]) runOption(options[0])
  }

  return (
    <>
      <Autocomplete<OmniboxOption, false, false, true>
        freeSolo
        autoHighlight
        clearOnBlur={false}
        value={null}
        inputValue={inputValue}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={() => setIsOpen(false)}
        onInputChange={(_event, value, reason) => {
          if (reason === 'reset') return
          setInputValue(value)
        }}
        onChange={handleChange}
        options={options}
        filterOptions={(items) => items}
        {...(inlineResults ? { PopperComponent: InlineResults, componentsProps: { paper: { elevation: 0 } } } : {})}
        groupBy={(option) => option.kind === 'action' ? t`Actions` : option.kind === 'patient' ? t`Patients` : 'Elara'}
        getOptionLabel={optionLabel}
        loading={patientSearch.isSearching}
        loadingText={t`Searching…`}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={optionKey(option)} sx={{ minHeight: 52 }}>
            <OptionContent option={option} />
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus={autoFocus}
            inputRef={inputRef}
            placeholder={canAskElara ? t`Type a word, a patient's name or a question for Elara…` : t`Type a word or a patient's name…`}
            inputProps={{ ...params.inputProps, 'aria-label': t`What would you like to do?` }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  <OmniboxInputTools
                    dictation={isDictationSupported() ? dictation : undefined}
                    onAttach={canAskElara ? attach : undefined}
                  />
                  {params.InputProps.endAdornment}
                </>
              ),
              // Room for the clear button + mic + paperclip on the right.
              sx: { fontSize: '1.1rem', borderRadius: '12px', minHeight: 60, '&&': { pr: '128px' }, '& .MuiAutocomplete-endAdornment': { display: 'flex', alignItems: 'center' } },
            }}
          />
        )}
      />
      <OmniboxStatus dictation={dictation} attachments={attachments} onRemove={(index) => setAttachments((current) => current.filter((_, i) => i !== index))} />
      {/* freeSolo hides the popup when empty, so say it here instead. */}
      {showNoResult && (
        <Typography role="status" color="text.secondary" sx={{ mt: 1.5 }}>
          {t`No result. Try another word, for example "patient" or "agenda".`}
        </Typography>
      )}
      {suggestions.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
          {suggestions.map((action) => {
            const Icon = action.icon
            return (
              <Chip
                key={action.id}
                icon={<Icon />}
                label={action.label}
                onClick={() => goTo(action.link)}
                variant="outlined"
                color="primary"
                sx={{ fontSize: '0.95rem', height: 40, px: 0.5, borderRadius: '20px' }}
              />
            )
          })}
        </Stack>
      )}
    </>
  )
}
